'use client';

import { useRef, useMemo, useLayoutEffect } from "react";
import { getOverlaidColor } from "../../../themes/utils/getOverlaidColor.js";
import { getContrast } from "../../../themes/utils/getContrast.js";
import { isDark } from "../../../themes/utils/isDark.js";
import { mergeRefs } from "../../../utils/props/mergeRefs.js";
import { logError } from "../../../utils/console/logError.js";
import { hoverOverlayVar, pressedOverlayVar, overlayFgColorAttr, hoverOverlays, pressedOverlays } from "./constants.js";
function readThemeColors(el) {
  const bg = window.getComputedStyle(el).getPropertyValue("background-color");
  const fgEl = el.querySelector(`[${overlayFgColorAttr}]`);
  if (!fgEl) {
    logError("Cannot determine button foreground color!");
    return [bg, bg];
  }
  const fg = window.getComputedStyle(fgEl).getPropertyValue("color");
  return [bg, fg];
}
function getOverlayKey(isBgDark, isFgDark) {
  if (isBgDark && isFgDark) return "onDarkWithDarkFg";
  if (isBgDark && !isFgDark) return "onDarkWithLightFg";
  if (!isBgDark && isFgDark) return "onLightWithDarkFg";
  return "onLightWithLightFg";
}
const contrastThresholdDefinedInFigmaDesigns = 1.08;
function getOverlaidContrast(bg, hover, pressed) {
  const hoverOverlaid = getOverlaidColor(bg, hover);
  const pressedOverlaid = getOverlaidColor(bg, pressed);
  return getContrast(hoverOverlaid, pressedOverlaid);
}
function pickLighteningOverlay([primary, fallback], onDarkProp) {
  return onDarkProp ? primary : fallback;
}
function pickDarkeningOverlay([primary, fallback], onDarkProp) {
  return onDarkProp ? fallback : primary;
}
function deriveOverlays(onDarkProp, [bg, fg]) {
  const isDarkFg = isDark(fg);
  const overlayKey = getOverlayKey(onDarkProp, isDark(fg));
  const hoverOverlayPair = hoverOverlays[overlayKey];
  const pressedOverlayPair = pressedOverlays[overlayKey];
  const [hoverOverlayPrimary, hoverOverlayFallback] = hoverOverlayPair;
  const [pressedOverlayPrimary, pressedOverlayFallback] = pressedOverlayPair;
  let hoverOverlay = hoverOverlayPrimary;
  let pressedOverlay = pressedOverlayPrimary;
  const primaryContrast = getOverlaidContrast(
    bg,
    hoverOverlayPrimary,
    pressedOverlayPrimary
  );
  if (primaryContrast < contrastThresholdDefinedInFigmaDesigns) {
    const fallbackContrast = getOverlaidContrast(
      bg,
      hoverOverlayFallback,
      pressedOverlayFallback
    );
    if (fallbackContrast > primaryContrast) {
      hoverOverlay = hoverOverlayFallback;
      pressedOverlay = pressedOverlayFallback;
    }
    if (fallbackContrast === primaryContrast) {
      const pick = isDarkFg ? pickLighteningOverlay : pickDarkeningOverlay;
      hoverOverlay = pick(hoverOverlayPair, onDarkProp);
      pressedOverlay = pick(pressedOverlayPair, onDarkProp);
    }
  }
  return [hoverOverlay, pressedOverlay];
}
const doNothing = () => {
};
function useRuntimeOverlays(refArg, p) {
  const fallbackRef = useRef(null);
  const mergedRef = useMemo(
    () => mergeRefs(refArg, fallbackRef),
    [fallbackRef, refArg]
  );
  useLayoutEffect(
    () => {
      if (!fallbackRef.current) return doNothing;
      const isUsingThemeColor = p.variant === "primary";
      if (!isUsingThemeColor) {
        fallbackRef.current.style.removeProperty(hoverOverlayVar);
        fallbackRef.current.style.removeProperty(pressedOverlayVar);
        return doNothing;
      }
      const setOverlayVars = () => {
        if (!fallbackRef.current) return;
        const themeColors = readThemeColors(fallbackRef.current);
        const [hoverOverlay, pressedOverlay] = deriveOverlays(
          p.onDark ?? false,
          themeColors
        );
        fallbackRef.current.style.cssText += `
          ${hoverOverlayVar}: ${hoverOverlay};
          ${pressedOverlayVar}: ${pressedOverlay};
        `;
      };
      setOverlayVars();
      fallbackRef.current.addEventListener("transitionend", setOverlayVars);
      return () => {
        var _a;
        (_a = fallbackRef.current) == null ? void 0 : _a.removeEventListener(
          "transitionend",
          setOverlayVars
        );
      };
    }
    // this effect needs to be run on every render (to support theme changes)
  );
  return mergedRef;
}
export {
  useRuntimeOverlays
};
